import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { X, Send, ClipboardList, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import './Mesas.css';

const Mesas = () => {
  const queryClient = useQueryClient();
  const [mesaSelecionada, setMesaSelecionada] = useState(null);

  const { data: estabelecimento } = useQuery(
    'estabelecimento-garcom',
    async () => {
      try {
        const response = await api.get('/api/estabelecimentos/me/estabelecimento');
        return response.data.data.estabelecimento;
      } catch (error) {
        if (error.response?.status === 404) {
          try {
            const response = await api.get('/api/estabelecimentos/padrao');
            return response.data.data.estabelecimento;
          } catch {
            return null;
          }
        }
        return null;
      }
    }
  );

  const { data: comandas, isLoading } = useQuery(
    ['comandas-abertas', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return [];
      const response = await api.get(`/api/comandas?estabelecimento=${estabelecimento._id}&status=aberta`);
      return response.data.data.comandas || [];
    },
    {
      enabled: !!estabelecimento?._id,
      refetchInterval: 5000
    }
  );

  const mesas = useMemo(() => {
    const lista = Array.isArray(comandas) ? comandas : [];
    const mapa = new Map();

    for (const c of lista) {
      const mesa = c?.mesa || 'SEM MESA';
      if (!mapa.has(mesa)) {
        mapa.set(mesa, { mesa, comandas: [], itens: 0, total: 0 });
      }
      const item = mapa.get(mesa);
      item.comandas.push(c);
      item.itens += Array.isArray(c?.itens) ? c.itens.length : 0;
      item.total += Number(c?.total || 0);
    }

    return Array.from(mapa.values()).sort((a, b) => String(a.mesa).localeCompare(String(b.mesa), 'pt-BR'));
  }, [comandas]);

  const enviarProducaoMutation = useMutation(
    (comandaId) => api.post(`/api/comandas/${comandaId}/enviar-producao`),
    {
      onSuccess: async () => {
        toast.success('Comanda enviada para produção!');
        await queryClient.invalidateQueries(['comandas-abertas']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao enviar para produção');
      }
    }
  );

  const mesaAtual = useMemo(() => {
    if (!mesaSelecionada) return null;
    return mesas.find((m) => m.mesa === mesaSelecionada) || null;
  }, [mesaSelecionada, mesas]);

  return (
    <div className="mesas-page">
      <div className="mesas-header">
        <div>
          <h2>
            <Users size={20} style={{ marginRight: 8 }} />
            Mesas
          </h2>
          <p>Selecione uma mesa para ver as comandas abertas</p>
        </div>
      </div>

      {isLoading ? (
        <div className="loading-state">Carregando mesas...</div>
      ) : mesas.length === 0 ? (
        <div className="empty-state">
          <ClipboardList size={40} />
          <p>Nenhuma comanda aberta no momento</p>
        </div>
      ) : (
        <div className="mesas-grid">
          {mesas.map((m) => (
            <button
              key={m.mesa}
              className="mesa-card"
              onClick={() => setMesaSelecionada(m.mesa)}
              type="button"
            >
              <div className="mesa-card-title">{m.mesa}</div>
              <div className="mesa-card-meta">
                <div>Comandas: {m.comandas.length}</div>
                <div>Itens: {m.itens}</div>
                <div>Total: R$ {m.total.toFixed(2)}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {mesaAtual && (
        <div className="modal-overlay" onClick={() => setMesaSelecionada(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Mesa: {mesaAtual.mesa}</h3>
              <button className="btn-close-modal" onClick={() => setMesaSelecionada(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              {mesaAtual.comandas.map((c) => (
                <div key={c._id} className="mesa-comanda">
                  <div className="mesa-comanda-head">
                    <div>
                      <strong>Comanda #{c.numero}</strong>
                      <div className="mesa-comanda-sub">Cliente: {typeof c.cliente === 'object' ? (c.cliente?.nome || 'Cliente') : (c.cliente || 'Cliente')}</div>
                    </div>
                    <div className="mesa-comanda-actions">
                      <button
                        className="btn-enviar-producao"
                        onClick={() => enviarProducaoMutation.mutate(c._id)}
                        disabled={enviarProducaoMutation.isLoading}
                        type="button"
                      >
                        <Send size={16} />
                        Enviar
                      </button>
                    </div>
                  </div>

                  <div className="mesa-comanda-itens">
                    {(c.itens || []).map((it, idx) => (
                      <div key={`${c._id}-${idx}`} className="mesa-item">
                        <div>
                          <strong>{it.quantidade}x</strong> {it.produto?.nome || 'Produto'}
                          {it.clienteItem ? <div className="mesa-item-sub">👤 {it.clienteItem}</div> : null}
                          {it.observacoes ? <div className="mesa-item-sub">📝 {it.observacoes}</div> : null}
                        </div>
                        <div>R$ {Number(it.subtotal || 0).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mesas;
